import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ClientSponsor } from '@ats/api/cm';
import { AtsUtilityService } from '@ats/utility';
import { Apollo, gql, QueryRef } from 'apollo-angular';
import { BehaviorSubject, combineLatest, Observable, of, throwError } from 'rxjs';
import {
	catchError,
	distinctUntilChanged,
	filter,
	first,
	map,
	skip,
	switchMap,
} from 'rxjs/operators';
import { CmInterceptHeaders } from '../models';
import { CmAuthService } from './auth.service';
import { CmSettingsService } from './settings.service';

const QUERY_CLIENT_FEATURES = gql`
	query featuresService__features {
		clientSponsor {
			name
			currentAccountFeatures {
				id
				name
				key
			}
		}
	}
`;

enum FeaturesActionTypes {
	UPDATE_PUBLIC_FEATURES = 'Update public features',
	UPDATE_PROTECTED_FEATURES = 'Update protected features',
}

interface FeaturesAction {
	readonly type: FeaturesActionTypes;
	readonly payload: string[] | null;
}

class FeaturesState {
	publicFeatures: string[] = null;
	protectedFeatures: string[] | null = null;
}

const reduce = (state: FeaturesState, action: FeaturesAction) => {
	switch (action.type) {
		case FeaturesActionTypes.UPDATE_PUBLIC_FEATURES:
			return {
				...state,
				publicFeatures: [...(action.payload as string[])],
			};

		case FeaturesActionTypes.UPDATE_PROTECTED_FEATURES:
			return {
				...state,
				protectedFeatures: action.payload ? [...(action.payload as string[])] : null,
			};
	}
};

@Injectable({
	providedIn: 'root',
})
export class CmFeatureService {
	private publicFeaturesHaveBeenSet = false;
	protected state = new FeaturesState();
	protected dispatch = new BehaviorSubject<FeaturesState>(this.state);

	public$: Observable<string[]> = this.dispatch.asObservable().pipe(
		map(state => state.publicFeatures),
		distinctUntilChanged()
	);

	protected$: Observable<string[]> = this.dispatch.asObservable().pipe(
		map(state => state.protectedFeatures),
		distinctUntilChanged()
	);

	features$: Observable<string[]> = combineLatest([this.public$, this.protected$]).pipe(
		map(([publicFeatures, protectedFeatures]) => [
			...(publicFeatures || []),
			...(protectedFeatures || []),
		]),
		filter(features => features.length > 0)
	);

	constructor(
		private auth: CmAuthService,
		private apollo: Apollo,
		private settings: CmSettingsService,
		private http: HttpClient,
		protected utilities: AtsUtilityService
	) {
		const featureQuery = this.fetchProtectedFeatures$();

		const features$ = featureQuery.valueChanges.pipe(
			map(results =>
				this.utilities.reduceNameKey(
					((results.data || {}).clientSponsor || {}).currentAccountFeatures || []
				)
			)
		);

		this.auth.token$
			.pipe(
				filter(token => !!token),
				skip(1)
			)
			.subscribe(() => {
				featureQuery.refetch();
			});

		this.auth.token$
			.pipe(switchMap(token => (token ? features$ : of(null))))
			.subscribe((features: string[] | null) => this.updateProtectedFeatures(features));
	}

	public fetchPublicFeatures$(client: string): Observable<string[]> {
		if (!this.publicFeaturesHaveBeenSet) {
			this.publicFeaturesHaveBeenSet = true;
			this.http
				.get<{ name: string; key: string }[]>(`${this.settings.api}/${client}/features`, {
					headers: CmInterceptHeaders.BypassAuth,
				})
				.pipe(
					map(features =>
						// TODO: Refactor this into an object instead of array for better performance
						features.reduce(
							(_features: string[], feature) =>
								feature.key !== feature.name
									? _features.concat(feature.key, feature.name)
									: _features.concat(feature.name),
							[]
						)
					),
					catchError(err => {
						throwError(err);
						return of([]);
					}),
					first()
				)
				.subscribe(features => this.updatePublicFeatures(features));
		}

		return this.public$.pipe(
			filter(features => features !== null),
			first()
		);
	}

	/**
	 * Load the current client features
	 * @private
	 * @method
	 * @return {Observable<string[]>} - Returns an observable containing a string array of features enabled for the client
	 */
	private fetchProtectedFeatures$(): QueryRef<{ clientSponsor: ClientSponsor }> {
		return this.apollo.watchQuery<{ clientSponsor: ClientSponsor }>({
			query: QUERY_CLIENT_FEATURES,
		});
	}

	/**
	 * Updates the public features in state
	 * @private
	 * @method
	 * @param {string[] | null} features - The public features to update in state
	 * @return {void}
	 */
	private updatePublicFeatures(features: string[] | null): void {
		const type = FeaturesActionTypes.UPDATE_PUBLIC_FEATURES;
		const action = { payload: features, type };
		this.dispatch.next((this.state = reduce(this.state, action)));
	}

	/**
	 * Updates the protected features in state
	 * @private
	 * @method
	 * @param {string[] | null} features - The protected features to update in state
	 * @return {void}
	 */
	private updateProtectedFeatures(features: string[] | null): void {
		const type = FeaturesActionTypes.UPDATE_PROTECTED_FEATURES;
		const action = { payload: features, type };
		this.dispatch.next((this.state = reduce(this.state, action)));
	}
}
